import { AfterViewInit, Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Applies to every text-like <input> (no template changes needed).
 *
 * Non-password fields:
 *  - no leading spaces (blocked while typing)
 *  - no double spaces (blocked while typing)
 *  - trailing spaces are removed on blur / Enter
 *    (so "Firstname Lastname" can still be typed naturally)
 *
 * Password fields:
 *  - no whitespace at all (blocked while typing, pasted, or autofilled)
 *  - still detected as a password when a show/hide toggle switches
 *    the type to "text" (see isPassword()).
 */
@Directive({
    selector:
        'input:not([type=checkbox]):not([type=radio]):not([type=file]):not([type=hidden])',
    standalone: true,
})
export class TrimInputDirective implements AfterViewInit {
    private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

    /** Remembers that this input has been a password field at some point. */
    private seenAsPassword = false;

    ngAfterViewInit(): void {
        // [type] bindings are applied by now, so this catches the initial "password" type
        this.isPassword();
    }

    @HostListener('input')
    onInput(): void {
        const input = this.el.nativeElement;

        const cleaned = this.isPassword()
            ? input.value.replace(/\s/g, '')
            : input.value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');

        if (cleaned !== input.value) {
            this.update(cleaned);
        }
    }

    @HostListener('blur')
    @HostListener('keydown.enter')
    onTrim(): void {
        if (this.isPassword()) return;

        const input = this.el.nativeElement;
        const trimmed = input.value.trim();
        if (trimmed !== input.value) {
            this.update(trimmed);
        }
    }

    /**
     * A field counts as a password if ANY of these is true:
     *  - its type is (or ever was) "password"
     *  - its id / name / formControlName contains "password"
     *  - its autocomplete is current-password / new-password
     * This keeps working when a show/hide toggle flips the type to "text".
     */
    private isPassword(): boolean {
        if (this.seenAsPassword) return true;

        const input = this.el.nativeElement;
        const hint = [
            input.id,
            input.name,
            input.getAttribute('formControlName'),
        ]
            .filter(Boolean)
            .join(' ');

        const autocomplete = input.autocomplete;

        if (
            input.type === 'password' ||
            /pass(word|wd)/i.test(hint) ||
            autocomplete === 'current-password' ||
            autocomplete === 'new-password'
        ) {
            this.seenAsPassword = true;
        }

        return this.seenAsPassword;
    }

    private update(value: string): void {
        const input = this.el.nativeElement;
        const oldLength = input.value.length;

        // selectionStart is null for some input types (e.g. email, number)
        const caret = input.selectionStart;

        input.value = value;

        if (caret !== null) {
            const newCaret = Math.max(0, caret - (oldLength - value.length));
            try {
                input.setSelectionRange(newCaret, newCaret);
            } catch {
                // some input types don't support selection; ignore
            }
        }

        // Lets Angular forms (reactive or ngModel) pick up the cleaned value.
        // Only dispatched when the value actually changed, so no event loop.
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }
}